import {
  Button,
  DropdownButton,
  FieldText,
  FieldTextarea,
  FlexBox,
  FormField,
  FormGrid,
  IconButton,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { AsyncStatus } from 'lib-shared-types-api';
import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { notificationActions } from 'components/notifications/actions/notification';
import { useAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from '../../feedbackModal.module.scss';

/**
 * Webform endpoint from the Product organization.
 *
 * @see https://support.dixa.help/en/articles/175-webform-endpoints
 */
const webformUrl =
  'https://forms.dixa.io/v2/forms/IM0Tyf0pSsRwt38Tc0d30/1uK5UgZYfmumakvRL1HTTG';

/** Webform endpoint allows a maximum of 5 files. */
const webformFileLimit = 5;

/** Represents the form of the feedback modal. */
export const FeedbackModalForm = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { modal, hide, meta } = useAppModal('app:feedbackForm');

  const {
    id: userId,
    name: userName,
    email: userEmail,
  } = useAppSelector((state) => state.settings.profile.profile);
  const {
    id: orgId,
    name: orgName,
    subdomain,
  } = useAppSelector((state) => state.settings.organization.organization);

  const { pathname } = useLocation();

  const [requestStatus, setRequestStatus] =
    useState<AsyncStatus>('uninitialized');

  const [senderEmail, setSenderEmail] = useState<string>(userEmail || '');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackAttachments, setFeedbackAttachments] =
    useState<FileList | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    customMessage,
    extra,
    team,
    title,
    disableAttachments = false,
    messageRequired = true,
    feature,
  } = modal.arg;

  const renderAttachments = (): JSX.Element => {
    buildFormData();
    // A hacky implementation to handle the webform limitation of 5 attachments.
    const attachments = [...(feedbackAttachments ?? [])].slice(
      0,
      webformFileLimit,
    );

    const label =
      attachments.length === 0
        ? 'No attached images'
        : `${attachments.length} ${
            attachments.length === 1 ? 'image' : 'images'
          } attached`;

    return (
      <FlexBox>
        <DropdownButton
          icon="file"
          buttonProps={{
            variant: 'tertiary',
            disabled: !attachments.length,
          }}
          dropdown={
            <FlexBox width="300px" padding="250" flexDirection="column">
              {attachments.map((file, index) => (
                <div className={styles.fileName} key={index}>
                  {file.name}
                </div>
              ))}
            </FlexBox>
          }
          fixed
        >
          {label}
        </DropdownButton>

        {attachments.length > 0 && (
          <Tooltip content="Remove all attachments">
            <IconButton
              variant="tertiary"
              icon="trash"
              onClick={() => setFeedbackAttachments(null)}
            />
          </Tooltip>
        )}
      </FlexBox>
    );
  };

  const buildFormData = (): FormData => {
    /** The webform endpoint expects FormData. */
    const formData: FormData = new FormData();
    const context: Record<string, string> = {
      userId,
      userName: userName || 'No name',
      orgId,
      orgName,
      subdomain,
      path: pathname,
      team,
      ...(feature && { feature }),
    };

    formData.append('email', senderEmail);
    formData.append('subject', `${title} (In-app)`);
    // The hidden input attached to the conversation message is a way to allow teams to route
    // conversations based on their team name or the URL where the feedback originates from. This
    // could for example be used to add a specific tag or queue to the conversation. The `team`
    // value is also used for determining which Slack channel the feedback should be sent to.
    const routingHelper = `<input type="hidden" value="team:${team};url:${pathname}"/>`;
    formData.append('message', `${feedbackMessage} ${routingHelper}`);

    // Context is sent as an internal note to the feedback email.
    formData.append(
      'Context',
      `<pre>${JSON.stringify(context, null, 2)}</pre>`,
    );
    // Extra is sent as an internal note to the feedback email.
    if (extra) {
      formData.append('Extra', `<pre>${JSON.stringify(extra, null, 2)}</pre>`);
    }

    // Appending the whole FileList (spread or in other ways) to the form does not work,
    // therefore this loop is established to append file by file
    if (feedbackAttachments) {
      for (let i = 0; i < webformFileLimit; i++) {
        formData.append('attachments', feedbackAttachments[i]);
      }
    }

    return formData;
  };

  const showNotification = ({
    isSuccessful,
  }: {
    isSuccessful: boolean;
  }): void => {
    dispatch(
      notificationActions.notificationAdd({
        title: isSuccessful ? 'Feedback sent' : 'Something went wrong',
        text: isSuccessful
          ? 'Thank you for helping us improve!'
          : 'Please try again or contact support. Your message is copied to clipboard.',
        className: isSuccessful ? 'success' : 'danger',
        icon: isSuccessful ? 'i-done' : 'i-exclamation',
        time: 5000,
      }),
    );
  };

  const handleSendFeedback = async (): Promise<void> => {
    setRequestStatus('pending');

    try {
      const { status } = await fetch(webformUrl, {
        method: 'POST',
        body: buildFormData(),
      });
      const isSuccess = status === 201;

      if (!isSuccess) {
        setRequestStatus('rejected');
        navigator.clipboard.writeText(feedbackMessage);
        showNotification({ isSuccessful: false });
      } else {
        setRequestStatus('fulfilled');
        showNotification({ isSuccessful: true });
      }

      hide?.();

      if (meta) {
        meta.resolve({
          isSent: isSuccess,
          isCancelled: false,
          feedbackMessage,
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          title,
          team,
          extra,
        },
        tags: {
          team: SentryTeam.Insights,
        },
      });
    }
  };

  const hasMessageIfRequired = messageRequired ? !!feedbackMessage : true;
  const canSend = hasMessageIfRequired && senderEmail;

  return (
    <FormGrid>
      <FormField>
        <FieldText
          label="Email"
          input={{
            value: senderEmail,
            name: 'userEmail',
            onChange: (e) => {
              if (e) setSenderEmail(e.currentTarget.value);
            },
          }}
          required
          noAlert
        />
      </FormField>

      <FormField>
        <FieldTextarea
          className={styles.feedbackMessageTextArea}
          label="Message"
          height={300}
          input={{
            value: feedbackMessage,
            name: 'message',
            onChange: (e) => setFeedbackMessage(e?.target.value || ''),
          }}
          placeholder={modal.arg.placeholder}
          autoFocus={true}
          required={messageRequired}
          noAlert
        />
      </FormField>

      {!disableAttachments && (
        <FormField>
          <FlexBox flexDirection="row" gap="400">
            <Tooltip content="Select max 5 images at once.">
              <Button
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                icon="cloud-upload"
                variant="secondary"
              >
                <input
                  className={styles.fileInputTag}
                  type="file"
                  name="attachments"
                  accept=".png, .jpg, .jpeg"
                  onChange={(e) => setFeedbackAttachments(e.target.files)}
                  onClick={(event) => {
                    const element = event.target as HTMLInputElement;
                    element.value = '';
                  }}
                  multiple
                  ref={fileInputRef}
                />
                Upload images
              </Button>
            </Tooltip>

            {renderAttachments()}
          </FlexBox>
        </FormField>
      )}

      <FormField>
        {customMessage && <FormField span={5}>{customMessage}</FormField>}

        <FlexBox justifyContent="flex-end">
          <Button
            variant="primary"
            onClick={handleSendFeedback}
            disabled={!canSend}
            isLoading={requestStatus === 'pending'}
          >
            Send
          </Button>
        </FlexBox>
      </FormField>
    </FormGrid>
  );
};
